import React, { useEffect } from "react";
import Animated, {
  Easing,
  useAnimatedProps,
  useSharedValue,
  withTiming,
} from "react-native-reanimated";
import { G, Path } from "react-native-svg";

import { BOARD_BG } from "../../theme/boardColors";
import { pathLength, spiralPath, spiralPoints } from "./markGeometry";
import { strokeWidthProps } from "./strokes";

const AnimatedPath = Animated.createAnimatedComponent(Path);

/** The short unwind at the reveal (spec §5); 0 under reduced motion. */
export const UNWIND_MS = 260;

interface AriadneMarkProps {
  x: number;
  y: number;
  r: number;
  color: string;
  /** Changes when she moves; the unwind replays. */
  at: string;
  reduced: boolean;
  unitsPerPx?: number;
}

/**
 * Her mark on her current word: cased in the board background like every
 * other stroke, still once drawn, and unwound once at each reveal. Never a
 * spinner.
 */
const AriadneMark: React.FC<AriadneMarkProps> = ({
  x,
  y,
  r,
  color,
  at,
  reduced,
  unitsPerPx = 1,
}) => {
  const d = spiralPath(r);
  const len = pathLength(spiralPoints(r));
  const offset = useSharedValue(reduced ? 0 : len);
  useEffect(() => {
    if (reduced) {
      offset.value = 0;
      return;
    }
    offset.value = len;
    offset.value = withTiming(0, {
      duration: UNWIND_MS,
      easing: Easing.out(Easing.cubic),
    });
  }, [at, reduced, len, offset]);
  const animated = useAnimatedProps(() => ({
    strokeDashoffset: offset.value,
  }));
  return (
    <G transform={`translate(${x},${y})`} testID="ariadne-mark">
      <Path
        d={d}
        fill="none"
        stroke={BOARD_BG}
        strokeLinecap="round"
        strokeLinejoin="round"
        {...strokeWidthProps(4.5, unitsPerPx)}
      />
      <AnimatedPath
        d={d}
        fill="none"
        stroke={color}
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeDasharray={`${len} ${len}`}
        animatedProps={animated}
        {...strokeWidthProps(2, unitsPerPx)}
      />
    </G>
  );
};

export default AriadneMark;
